import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalDawLogos,
  signalFonts,
  signalRadii,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  DawTile,
  GlassCard,
  Mono,
  ScreenFrame,
  SignalButton,
  StepRail,
  StatusPill,
  Waveform,
} from '@/common/components/signal';
import { detected, stemsWave } from '../fixtures';

const STEPS = ['STEP 1', 'STEP 2'];

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="h3"
    sx={{
      margin: '0 0 6px',
      fontFamily: signalFonts.display,
      fontWeight: 800,
      fontSize: '30px',
      letterSpacing: '-0.03em',
    }}
  >
    {children}
  </Box>
);

/** Shared footer: a mono status on the left, cancel + commit on the right. */
const CardFooter: React.FC<{
  status: React.ReactNode;
  backLabel: string;
  actionLabel: string;
}> = ({ status, backLabel, actionLabel }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '24px',
    }}
  >
    {status}
    <Box sx={{ display: 'flex', gap: '10px' }}>
      <SignalButton variant="ghost" size="lg">
        {backLabel}
      </SignalButton>
      <SignalButton size="lg" glow={false} sx={{ padding: '10px 18px' }}>
        {actionLabel}
      </SignalButton>
    </Box>
  </Box>
);

const CHOICE_BODY = {
  fontSize: '12.5px',
  lineHeight: 1.55,
  color: signalText.secondary,
} as const;

/**
 * 03 — Upload flow.
 *
 * Step 1 asks the one question that changes everything downstream: is this a
 * DAW session or loose audio? The two cards answer it visually — a row of DAW
 * icons versus a bare waveform — so the choice can be made without reading the
 * body copy. Step 2 then reports what the scan actually found, pre-checked, so
 * the default action is correct and the user only intervenes to exclude.
 */
export const Screen03Upload: React.FC = () => (
  <ScreenFrame
    index="03"
    title="Upload flow"
    note="STEP 1 CHOOSER · STEP 2 DETECTED"
    bare
  >
    <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
      <GlassCard>
        <StepRail steps={STEPS} active={0} sx={{ marginBottom: '12px' }} />
        <Title>What are you uploading?</Title>
        <Box
          component="p"
          sx={{
            margin: '0 0 24px',
            fontSize: '13px',
            color: signalText.secondary,
            lineHeight: 1.6,
          }}
        >
          We&apos;ll scan the folder, detect sessions and keep your structure
          intact.
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '14px',
          }}
        >
          <Box
            sx={{
              border: `1px solid ${signalColors.accent}`,
              borderRadius: `${signalRadii['2xl']}px`,
              background: 'rgba(255,106,31,0.08)',
              padding: '20px',
              boxShadow: '0 0 0 3px rgba(255,106,31,0.14)',
              cursor: 'pointer',
            }}
          >
            <Box sx={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
              <DawTile
                src={signalDawLogos.Ableton}
                label="Ableton"
                size={30}
                radius={8}
              />
              <DawTile
                src={signalDawLogos.LogicPro}
                label="Logic Pro"
                size={30}
                radius={8}
              />
              <DawTile
                src={signalDawLogos.FLStudio}
                label="FL Studio"
                size={30}
                radius={8}
              />
              <Box
                component="span"
                sx={{
                  width: 30,
                  height: 30,
                  flex: 'none',
                  borderRadius: '8px',
                  background: signalSurfaces.fillHover,
                  color: signalText.secondary,
                  fontFamily: signalFonts.mono,
                  fontWeight: 600,
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                +5
              </Box>
            </Box>

            <Box
              sx={{
                fontFamily: signalFonts.display,
                fontWeight: 700,
                fontSize: '19px',
                letterSpacing: '-0.02em',
                marginBottom: '6px',
              }}
            >
              Project files
            </Box>
            <Box sx={CHOICE_BODY}>
              A DAW session folder — .als, .logicx, .ptx, .flp, .cpr, .rpp.
              Versions, samples and renders come along.
            </Box>
            <Mono
              size={10}
              weight={600}
              color={signalColors.accentBright}
              sx={{ display: 'block', marginTop: '14px' }}
            >
              SELECTED ✓
            </Mono>
          </Box>

          <Box
            sx={{
              border: `1px solid rgba(255,255,255,0.11)`,
              borderRadius: `${signalRadii['2xl']}px`,
              background: signalSurfaces.fillWeak,
              padding: '20px',
              cursor: 'pointer',
              '&:hover': { borderColor: 'rgba(255,255,255,0.24)' },
            }}
          >
            <Waveform
              bars={stemsWave}
              height={30}
              barWidth={3}
              sx={{ marginBottom: '16px' }}
            />
            <Box
              sx={{
                fontFamily: signalFonts.display,
                fontWeight: 700,
                fontSize: '19px',
                letterSpacing: '-0.02em',
                marginBottom: '6px',
              }}
            >
              Stems folder
            </Box>
            <Box sx={CHOICE_BODY}>
              Loose audio — wav, aiff, mp3. Stored as a stem pack with waveform
              previews and no DAW dependency.
            </Box>
            <Mono
              size={10}
              weight={600}
              sx={{ display: 'block', marginTop: '14px' }}
            >
              CHOOSE
            </Mono>
          </Box>
        </Box>

        <CardFooter
          status={<Mono size={11}>DESKTOP AGENT · FAST S3 TRANSFER</Mono>}
          backLabel="Cancel"
          actionLabel="Choose folder"
        />
      </GlassCard>

      <GlassCard>
        <StepRail steps={STEPS} active={1} sx={{ marginBottom: '12px' }} />
        <Title>We found 3 sessions</Title>
        <Box
          component="p"
          sx={{
            margin: '0 0 20px',
            fontSize: '13px',
            color: signalText.secondary,
            lineHeight: 1.6,
          }}
        >
          In{' '}
          <Box
            component="span"
            sx={{ fontFamily: signalFonts.mono, color: signalText.primary }}
          >
            ~/Music/Ableton
          </Box>{' '}
          · 2.94 GB total. Uncheck anything you&apos;d rather leave out.
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {detected.map((d) => (
            <Box
              key={d.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '13px',
                border: `1px solid ${
                  d.checked ? signalBorders.accentHover : signalBorders.default
                }`,
                borderRadius: `${signalRadii.xl}px`,
                background: d.checked
                  ? 'rgba(255,106,31,0.07)'
                  : signalSurfaces.fillSubtle,
                padding: '14px 16px',
              }}
            >
              {/* Checked rows carry a filled accent box; unchecked ones an
                  empty outline — the same affordance either way. */}
              <Box
                component="span"
                role="checkbox"
                aria-checked={d.checked}
                aria-label={d.name}
                sx={{
                  width: 18,
                  height: 18,
                  flex: 'none',
                  borderRadius: `${signalRadii.xs}px`,
                  background: d.checked ? signalColors.accent : 'transparent',
                  border: `1px solid ${
                    d.checked ? signalColors.accent : 'rgba(255,255,255,0.2)'
                  }`,
                  color: signalColors.onAccent,
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {d.checked ? '✓' : ''}
              </Box>

              <DawTile src={d.logo} label={d.daw} size={34} radius={9} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 600, fontSize: '14px' }}>{d.name}</Box>
                <Mono size={10.5} sx={{ display: 'block', marginTop: '3px' }}>
                  {d.meta}
                </Mono>
              </Box>

              <StatusPill status={d.tagStatus}>{d.tag}</StatusPill>
            </Box>
          ))}
        </Box>

        <CardFooter
          status={
            <Mono size={11} color={signalText.secondary}>
              2 SELECTED · 2.31 GB · ~4 MIN
            </Mono>
          }
          backLabel="Back"
          actionLabel="Upload 2 sessions"
        />
      </GlassCard>
    </Box>
  </ScreenFrame>
);

export default Screen03Upload;
